'use client';
import { ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';

import { cn } from '@/shared/utils';

import type { CandidateTrack } from './candidate-report';

export function CandidateOverviewHero({
	title,
	description,
	onEvent,
}: {
	title: string;
	description: string;
	onEvent: CandidateTrack;
}) {
	const id = 'simulator';
	const [expanded, setExpanded] = useState(false);
	const toggle = (next: boolean) => {
		setExpanded(next);
		onEvent(next ? 'description_expand' : 'description_collapse');
	};
	return (
		<section
			aria-labelledby={`${id}-title`}
			className="relative isolate overflow-hidden rounded-xl bg-gradient-to-br from-red-lightBright/70 via-red-light to-white px-5 pb-2 pt-6 sm:px-8 sm:pb-3 sm:pt-8"
			onKeyDown={(event) => {
				if (event.key === 'Escape' && expanded) toggle(false);
			}}
		>
			<Image
				unoptimized
				src="/course-workspace/hero-ribbons.svg"
				alt=""
				aria-hidden="true"
				width={228}
				height={256}
				className="pointer-events-none absolute right-0 top-0 h-auto w-28 max-w-none select-none opacity-[0.12] md:w-36 md:opacity-60"
			/>
			<h1
				id={`${id}-title`}
				className="relative text-[26px] font-semibold leading-tight tracking-[-0.025em] sm:text-[32px] md:pr-44"
			>
				{title}
			</h1>
			<p
				id={`${id}-description`}
				className={cn(
					'relative mt-3 max-w-[720px] text-base leading-6 text-gray-3 md:max-w-[calc(100%-180px)]',
					!expanded &&
						'max-h-12 overflow-hidden [mask-image:linear-gradient(to_bottom,black_0%,black_35%,rgba(0,0,0,0.95)_50%,rgba(0,0,0,0.65)_67%,rgba(0,0,0,0.25)_83%,transparent_100%)]',
				)}
			>
				{description}
			</p>
			<button
				type="button"
				aria-label={expanded ? 'Свернуть описание' : 'Показать полное описание'}
				aria-expanded={expanded}
				aria-controls={`${id}-description`}
				onClick={() => toggle(!expanded)}
				className="mx-auto mt-1 flex h-6 w-8 cursor-pointer items-center justify-center rounded-lg text-gray-3 transition-colors after:absolute after:inset-0 after:rounded-xl hover:bg-white/70 hover:text-black focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-blue"
			>
				<ChevronDown
					size={20}
					aria-hidden="true"
					className={cn(
						'transition-transform motion-reduce:transition-none',
						expanded && 'rotate-180',
					)}
				/>
			</button>
		</section>
	);
}
